import type { MetaTag } from '../types';
import { defaultSeoConfig } from '../config/seo';
import { seoTranslations } from '../config/seo.i18n';
import { supportedLocales, type LocaleConfig } from '../config/locales';
import { config } from '../config/environment';

function truncateMetaDescription(description: string, max = 155): string {
  const text = String(description || '').replace(/\s+/g, ' ').trim()
  if (text.length <= max) return text
  const slice = text.slice(0, max - 1)
  const lastSpace = slice.lastIndexOf(' ')
  const cut = lastSpace > 80 ? slice.slice(0, lastSpace) : slice
  return `${cut}…`
}

class SeoService {
  private static instance: SeoService;
  private defaultTitle: string;
  private defaultDescription: string;
  private currentLocale: string = 'fr';
  private localeConfig: LocaleConfig;

  private constructor() {
    this.localeConfig = supportedLocales[this.currentLocale];
    this.defaultTitle = defaultSeoConfig.title;
    this.defaultDescription = defaultSeoConfig.description;
    this.initializeDefaultMeta();
  }

  static getInstance(): SeoService {
    if (!SeoService.instance) {
      SeoService.instance = new SeoService();
    }
    return SeoService.instance;
  }

  setLocale(_locale: string): void {
    this.currentLocale = 'fr';
    this.localeConfig = supportedLocales.fr;
    const translations = seoTranslations.fr;
    if (translations) {
      this.defaultTitle = translations.title;
      this.defaultDescription = translations.description;
    }
    this.initializeDefaultMeta();
  }

  private static stripLocalePrefix(path: string): string {
    let cleanPath = path.replace(/^\/(fr|en|es|de|it|pt|nl|pl)(\/|$)/, '/');
    if (cleanPath === '') cleanPath = '/';
    return cleanPath;
  }

  private getLocalizedUrl(path: string): string {
    const baseUrl = defaultSeoConfig.canonicalUrl.replace(/\/$/, '');
    let cleanPath = SeoService.stripLocalePrefix(path);
    if (cleanPath === '') cleanPath = '/';
    if (cleanPath === '/') return `${baseUrl}/`;
    if (!cleanPath.endsWith('/')) cleanPath = `${cleanPath}/`;
    return `${baseUrl}${cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`}`;
  }

  private initializeDefaultMeta(): void {
    const localizedConfig = seoTranslations[this.currentLocale] || seoTranslations['fr'];

    this.setMetaTags([
      { name: 'description', content: localizedConfig.description },
      { name: 'robots', content: config.environment === 'production' ? 'index,follow' : 'noindex,nofollow' },
      { name: 'language', content: this.localeConfig.code },
      { name: 'og:locale', content: this.localeConfig.code.replace('-', '_') },
    ]);

    let currentPath = window.location.pathname;
    currentPath = SeoService.stripLocalePrefix(currentPath);
    if (currentPath === '') currentPath = '/';
    const pageCanonical = this.getLocalizedUrl(currentPath);

    this.setOpenGraph({
      title: localizedConfig.title,
      description: localizedConfig.description,
      image: `${defaultSeoConfig.canonicalUrl}/og-image.jpg`,
      url: pageCanonical
    });

    this.setTwitterCard({
      card: defaultSeoConfig.twitterCard,
      site: '@postdev',
      title: localizedConfig.title,
      description: localizedConfig.description,
      image: `${defaultSeoConfig.canonicalUrl}/og-image.jpg`
    });

    this.setCanonicalUrl(pageCanonical);

    // Accueil : JSON-LD statiques dans index.html (Organization, LocalBusiness, WebSite, Service).
    // Évite la duplication Organization côté client sur /.
    if (currentPath !== '/') {
      const organizationData = {
        ...defaultSeoConfig.organization,
        areaServed: {
          '@type': 'Country',
          name: this.localeConfig.region
        },
        availableLanguage: Object.values(supportedLocales).map(loc => ({
          '@type': 'Language',
          name: loc.name,
          alternateName: loc.code
        }))
      };
      this.generateStructuredData(organizationData);
    } else {
      document.querySelector('script[data-seo-service="true"]')?.remove();
    }
  }

  /** Monolingue FR : alternates pointent vers la même URL que le canonical courant. */
  private generateLanguageAlternates(): Array<{ lang: string; url: string }> {
    const href = document.querySelector('link[rel="canonical"]')?.getAttribute('href');
    if (!href) return [];
    return [
      { lang: 'fr', url: href },
      { lang: 'x-default', url: href }
    ];
  }

  generateLocalizedStructuredData(data: Record<string, any>): Record<string, any> {
    const localizedConfig = seoTranslations.fr;
    const type = data['@type'] as string | undefined;
    const skipOrgOverlay = type && ['Article', 'BlogPosting', 'NewsArticle', 'CreativeWork'].includes(type);

    if (skipOrgOverlay) {
      return {
        ...data,
        inLanguage: data.inLanguage || this.localeConfig.code
      };
    }

    return {
      ...data,
      inLanguage: this.localeConfig.code,
      name: data.name || localizedConfig.organization.name,
      description: data.description || localizedConfig.organization.description,
      areaServed: {
        '@type': 'Country',
        name: this.localeConfig.region
      }
    };
  }

  setTitle(title: string): void {
    document.title = title;
    this.updateMetaTag('og:title', title);
    this.updateMetaTag('twitter:title', title);
  }

  setDescription(description: string): void {
    const truncated = truncateMetaDescription(description);
    this.updateMetaTag('description', truncated);
    this.updateMetaTag('og:description', truncated);
    this.updateMetaTag('twitter:description', truncated);
  }

  setMetaTags(tags: MetaTag[]): void {
    tags.forEach(tag => this.updateMetaTag(tag.name, tag.content));
  }

  setOpenGraph(data: {
    title: string
    description: string
    image: string
    url: string
    type?: string
    publishedTime?: string
    modifiedTime?: string
    author?: string
  }): void {
    const tags: MetaTag[] = [
      { name: 'og:title', content: data.title },
      { name: 'og:description', content: data.description },
      { name: 'og:image', content: data.image },
      { name: 'og:image:width', content: '1200' },
      { name: 'og:image:height', content: '630' },
      { name: 'og:url', content: data.url },
      { name: 'og:type', content: data.type || 'website' },
      { name: 'og:site_name', content: 'PostDev' },
      { name: 'og:locale', content: this.localeConfig.code.replace('-', '_') }
    ];
    if (data.publishedTime) {
      tags.push({ name: 'article:published_time', content: data.publishedTime });
    }
    if (data.modifiedTime) {
      tags.push({ name: 'article:modified_time', content: data.modifiedTime });
    }
    if (data.author) {
      tags.push({ name: 'article:author', content: data.author });
      tags.push({ name: 'author', content: data.author });
    }
    this.setMetaTags(tags);
    this.updateOgLocaleAlternates();
  }

  private updateOgLocaleAlternates(): void {
    document.querySelectorAll('meta[property="og:locale:alternate"]').forEach((el) => el.remove());
  }

  setTwitterCard(data: { card: string; site?: string; title: string; description: string; image: string }): void {
    this.setMetaTags([
      { name: 'twitter:card', content: data.card },
      { name: 'twitter:site', content: data.site ?? '@postdev' },
      { name: 'twitter:title', content: data.title },
      { name: 'twitter:description', content: data.description },
      { name: 'twitter:image', content: data.image },
      { name: 'twitter:image:alt', content: data.title }
    ]);
  }

  setCanonicalUrl(url: string): void {
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);
    this.updateMetaTag('og:url', url);
    this.updateMetaTag('twitter:url', url);
    this.updateLanguageAlternates();
  }

  setLanguageAlternates(alternates: { lang: string; url: string }[]): void {
    document.querySelectorAll('link[rel="alternate"][hreflang]').forEach(el => el.remove());

    alternates.forEach(alternate => {
      const link = document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', alternate.lang);
      link.setAttribute('href', alternate.url);
      document.head.appendChild(link);
    });
  }

  updateLanguageAlternates(): void {
    const alternates = this.generateLanguageAlternates();
    this.setLanguageAlternates(alternates);
  }

  private updateMetaTag(name: string, content: string): void {
    const useProperty = name.startsWith('og:');
    const attrName = useProperty ? 'property' : 'name';
    let meta = document.querySelector(`meta[${attrName}="${name}"]`);
    if (!meta && !useProperty && name.startsWith('twitter:')) {
      const legacy = document.querySelector(`meta[property="${name}"]`);
      if (legacy) {
        legacy.removeAttribute('property');
        legacy.setAttribute('name', name);
        meta = legacy;
      }
    }
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute(attrName, name);
      document.head.appendChild(meta);
    }
    meta.setAttribute('content', content);
  }

  /** Compatible CSP require-trusted-types-for 'script'. */
  private assignJsonLdContent(script: HTMLScriptElement, json: string): void {
    const tt = (window as any).trustedTypes as TrustedTypePolicyFactory | undefined;
    if (tt?.defaultPolicy) {
      script.textContent = tt.defaultPolicy.createScript(json) as unknown as string;
    } else {
      script.textContent = json;
    }
  }

  /**
   * Injecte un bloc JSON-LD distinct (ex. FAQPage) sans écraser le script principal.
   * @param markerAttr ex. data-seo-faq
   * @param markerValue ex. true
   */
  appendStructuredDataScript(
    data: Record<string, unknown>,
    markerAttr: string,
    markerValue: string
  ): void {
    document.querySelector(`script[${markerAttr}="${markerValue}"]`)?.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(markerAttr, markerValue);
    this.assignJsonLdContent(script, JSON.stringify(data));
    document.head.appendChild(script);
  }

  removeStructuredDataScript(markerAttr: string, markerValue: string): void {
    document.querySelector(`script[${markerAttr}="${markerValue}"]`)?.remove();
  }

  generateStructuredData(data: Record<string, any>): void {
    const localizedData = this.generateLocalizedStructuredData(data);

    const existing = document.querySelector('script[data-seo-service="true"]');
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-service', 'true');
    this.assignJsonLdContent(script, JSON.stringify(localizedData));
    document.head.appendChild(script);
  }
}

export const seoService = SeoService.getInstance();
